import { readAllRows } from "@/lib/read-all-rows";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, FolderOpen, Loader2, MessageSquare, Plus, Trash2, Share2, Eye } from "lucide-react";
import { ShareDialog } from "@/components/share/share-dialog";
import { ProjectWorkspaceFiles } from "@/components/workspace/project-workspace-files";
import { useResourceRole } from "@/hooks/use-resource-role";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { operation } from "@/lib/api-client";
import { aws as awsClient, getCsrfToken } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";

export const Route = createFileRoute("/projects/$projectId")({
  remountDeps: ({ params }) => params.projectId,
  head: () => ({
    meta: [
      { title: "Project — AskBell" },
      { name: "description", content: "Project knowledge base and chats." },
    ],
  }),
  component: ProjectPage,
});

type Project = {
  id: string;
  name: string;
  description: string | null;
  instructions: string | null;
  default_persona_id: string | null;
};

type PersonaOption = { id: string; name: string };

const NO_PERSONA_VALUE = "__none__";

type ConvRow = {
  id: string;
  title: string;
  description: string | null;
  updated_at: string;
};

function ProjectPage() {
  const { projectId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const { role: projectRole, error:roleError, refresh:refreshRole } = useResourceRole("project", projectId);
  const isOwner = projectRole === "owner";
  const canEdit = projectRole === "owner" || projectRole === "collaborator";
  const isViewer = projectRole === "viewer";

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const projectScope = useRef(projectId); projectScope.current = projectId;
  const [editingName, setEditingName] = useState("");
  const [editingDesc, setEditingDesc] = useState("");
  const [editingInstr, setEditingInstr] = useState("");
  const [editingDefaultPersonaId, setEditingDefaultPersonaId] = useState<string | null>(null);
  const metadataDirty = useRef(false);
  const [personas, setPersonas] = useState<PersonaOption[]>([]);
  const [savingMeta, setSavingMeta] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [convs, setConvs] = useState<ConvRow[]>([]);

  const refreshRequest = useRef(0);
  const refresh = useCallback(async () => {
    if (!user) return;
    const request = ++refreshRequest.current;
    setLoading(true);
    const current = () => projectScope.current === projectId && request === refreshRequest.current;
    const results = await Promise.all([
      awsClient.from("projects").select("id, name, description, instructions, default_persona_id").eq("id", projectId).maybeSingle(),
      readAllRows<ConvRow>((from,to) => awsClient.from("conversations").select("id, title, description, updated_at")
        .eq("project_id", projectId).order("updated_at", { ascending: false }).order("id", { ascending: true }).range(from,to), current),
      readAllRows<PersonaOption>((from,to) => awsClient.from("personas").select("id, name").order("name", { ascending: true }).order("id", { ascending: true }).range(from,to), current),
    ]);
    if (!current()) return;
    const failure = results.find(result => result.error)?.error;
    const [{data:proj},{data:convRows},{data:personaRows}] = results;
    if (failure) {setLoadError(failure.message);setLoading(false);return;}
    if (!proj) {
      toast.error("Project not found or no longer available.");
      void navigate({to:"/projects",replace:true});
      return;
    }
    setLoadError(null);
    setProject(proj as Project);
    if (!metadataDirty.current) {
      setEditingName(proj.name);
      setEditingDesc(proj.description ?? "");
      setEditingInstr(proj.instructions ?? "");
      setEditingDefaultPersonaId((proj as Project).default_persona_id ?? null);
    }
    setPersonas((personaRows ?? []) as PersonaOption[]);
    setConvs((convRows ?? []) as ConvRow[]);
    setLoading(false);
  }, [projectId, user, navigate]);

  useEffect(() => { refresh(); return () => { refreshRequest.current++; }; }, [refresh]);

  const handleSaveMeta = async () => {
    if (!project || !canEdit || savingMeta) return;
    if (!editingName.trim()) { toast.error("Give the project a name."); return; }
    setSavingMeta(true);
    try {
      await operation("projects_update", {
        id:project.id,
        name:editingName.trim(),
        description:editingDesc.trim() || null,
        instructions:editingInstr.trim() || null,
        defaultPersonaId:editingDefaultPersonaId,
      }, getCsrfToken());
      metadataDirty.current = false;
      toast.success("Project saved.");
      await refresh();
    } catch(error) {toast.error(error instanceof Error ? error.message : "Couldn't save project.");}
    finally {setSavingMeta(false);}
  };

  const [creatingChat, setCreatingChat] = useState(false);
  const chatRequestRef = useRef<{projectId:string;id:string} | null>(null);
  const handleNewProjectChat = async () => {
    if (!user || !project || !canEdit || creatingChat) return;
    setCreatingChat(true);
    try {
      const clientRequestId = chatRequestRef.current?.projectId === project.id ? chatRequestRef.current.id : crypto.randomUUID();
      chatRequestRef.current = {projectId:project.id,id:clientRequestId};
      const result = await operation<{clientRequestId:string;modelId:string;projectId:string},{conversationId:string}>("chat_prepare", {clientRequestId,modelId:"openai-direct/gpt-6-astra",projectId:project.id}, getCsrfToken());
      chatRequestRef.current = null;
      navigate({to:"/chat",search:{conversation:result.conversationId} as any});
    } catch (error) {toast.error(error instanceof Error ? error.message : "Couldn't start chat.");}
    finally {setCreatingChat(false);}
  };

  const handleDeleteProject = async () => {
    if (!project || !isOwner || deleting) return;
    setDeleting(true);
    try {
      await operation("projects_delete", {id:project.id}, getCsrfToken());
      setDeleteOpen(false);
      toast.success("Project deleted. Its chats and files have been retained.");
      navigate({ to: "/projects" });
    } catch(error) {toast.error(error instanceof Error ? error.message : "Couldn't delete project.");}
    finally {setDeleting(false);}
  };

  if (loadError) return <AppLayout><div className="mx-auto w-full max-w-4xl p-6">
    <Link to="/projects" className="mb-5 inline-flex items-center gap-1 text-ui-base text-muted-foreground hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" />All projects</Link>
    <div role="alert" className="rounded-ui-md border border-destructive/30 bg-destructive/5 p-5">
      <h1 className="font-bold">Couldn't open this project</h1>
      <p className="mt-1 text-ui-base text-muted-foreground">{loadError}</p>
      <Button className="mt-4" variant="outline" disabled={loading} onClick={()=>void refresh()}>{loading ? <><Loader2 className="h-4 w-4 animate-spin" />Loading…</> : "Try again"}</Button>
    </div>
  </div></AppLayout>;
  if (!project) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex items-center gap-2 text-ui-base">
            <Link to="/projects" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" />
              All projects
            </Link>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ui-md bg-primary/10 text-primary">
                <FolderOpen className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <h1 className="text-ui-xl font-bold text-foreground truncate">{project.name}</h1>
                {project.description && (
                  <p className="text-ui-base text-muted-foreground truncate">{project.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isOwner && (
                <Button variant="ghost" onClick={() => setShareOpen(true)} className="gap-1.5">
                  <Share2 className="h-4 w-4" /> Share
                </Button>
              )}
              {canEdit && (
                <Button onClick={handleNewProjectChat} disabled={creatingChat} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  New chat
                </Button>
              )}
            </div>
          </div>

          {roleError && <div role="alert" className="mb-4 rounded-ui-md border border-destructive/30 p-3 text-ui-base"><p>Could not check project access. {roleError}</p><Button variant="outline" size="sm" className="mt-2" onClick={refreshRole}>Try again</Button></div>}
          {isViewer && (
            <div className="mb-4 flex items-center gap-2 rounded-ui-md border border-border bg-secondary/40 px-4 py-2.5 text-ui-base text-muted-foreground">
              <Eye className="h-4 w-4 shrink-0" />
              <span>You're viewing this project as a guest. Ask the owner for edit access to change settings or organize chat workspaces.</span>
            </div>
          )}

          <Tabs defaultValue="chats">
            <TabsList>
              <TabsTrigger value="chats">Chats ({convs.length})</TabsTrigger>
              <TabsTrigger value="workspace">Workspace Files</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="chats" className="mt-4">
              {convs.length === 0 ? (
                <div className="rounded-ui-md border border-dashed border-border p-10 text-center text-muted-foreground">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-ui-base">No chats in this project yet.</p>
                  <Button variant="outline" size="sm" onClick={handleNewProjectChat} disabled={creatingChat || !canEdit} className="mt-3 gap-1.5">
                    <Plus className="h-3.5 w-3.5" /> Start the first chat
                  </Button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {convs.map((c) => (
                    <li key={c.id}>
                      <Link
                        to="/chat"
                        search={{ conversation: c.id } as any}
                        className="block rounded-ui-md border border-border hover:border-primary/40 hover:bg-secondary/40 transition-colors px-4 py-3"
                      >
                        <div className="text-ui-base font-bold text-foreground">
                          {c.title || "New conversation"}
                        </div>
                        {c.description && (
                          <p className="text-ui-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="workspace" className="mt-4">
              <ProjectWorkspaceFiles chats={convs} canEdit={canEdit} onCreateChat={() => void handleNewProjectChat()} creatingChat={creatingChat} />
            </TabsContent>

            <TabsContent value="settings" className="mt-4 space-y-4 max-w-2xl">
              <div>
                <label htmlFor="project-name" className="text-ui-base font-bold text-foreground">Name</label>
                <Input id="project-name" maxLength={160} disabled={!canEdit || savingMeta} value={editingName} onChange={(e) => { metadataDirty.current = true; setEditingName(e.target.value); }} className="mt-1" />
              </div>
              <div>
                <label htmlFor="project-description" className="text-ui-base font-bold text-foreground">Description</label>
                <Input
                  id="project-description"
                  maxLength={5000}
                  disabled={!canEdit || savingMeta}
                  value={editingDesc}
                  onChange={(e) => { metadataDirty.current = true; setEditingDesc(e.target.value); }}
                  className="mt-1"
                  placeholder="What's this project about?"
                />
              </div>
              <div>
                <label htmlFor="project-instructions" className="text-ui-base font-bold text-foreground">Instructions</label>
                <Textarea
                  id="project-instructions"
                  maxLength={100000}
                  disabled={!canEdit || savingMeta}
                  value={editingInstr}
                  onChange={(e) => { metadataDirty.current = true; setEditingInstr(e.target.value); }}
                  rows={6}
                  className="mt-1"
                  placeholder="System-level guidance applied to every chat in this project. e.g. &quot;You are helping evaluate the 2025 Acme RFP. Always reference the scoring rubric.&quot;"
                />
                <p className="text-ui-sm text-muted-foreground mt-1">
                  These instructions are injected into the system prompt for every chat in this project.
                </p>
              </div>
              <div>
                <label htmlFor="project-persona" className="text-ui-base font-bold text-foreground">Default persona</label>
                <Select
                  disabled={!canEdit || savingMeta}
                  value={editingDefaultPersonaId ?? NO_PERSONA_VALUE}
                  onValueChange={(v) => { metadataDirty.current = true; setEditingDefaultPersonaId(v === NO_PERSONA_VALUE ? null : v); }}
                >
                  <SelectTrigger id="project-persona" className="mt-1">
                    <SelectValue placeholder="AskBell default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PERSONA_VALUE}>AskBell default (no persona)</SelectItem>
                    {personas.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-ui-sm text-muted-foreground mt-1">
                  New chats in this project start with this persona. Individual chats can switch to a different one.
                </p>
              </div>
              <div className="flex justify-between items-center pt-2">
                <Button disabled={!isOwner} variant="destructive" onClick={() => setDeleteOpen(true)} className="gap-1.5">
                  <Trash2 className="h-4 w-4" />
                  Delete project
                </Button>
                <Button onClick={handleSaveMeta} disabled={savingMeta || !canEdit || !editingName.trim()}>
                  {savingMeta ? "Saving…" : "Save changes"}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              "{project.name}" will be removed. Its chats and their Workspace Files will remain intact as standalone chats.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => { e.preventDefault(); handleDeleteProject(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        resourceType="project"
        resourceId={projectId}
        resourceName={project?.name}
      />
    </AppLayout>
  );
}
